import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "./SERVER_URL";
import * as XLSX from "xlsx";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useAuth } from "src/components/auth/AuthContext";
import toast from "react-hot-toast";

// ─── Responses Modal ──────────────────────────────────────────────────────────
const ResponsesModal = ({ submission, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const responses = submission.responses || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-modalIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              Responses
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium">
                #{submission.submission_id}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium">
                {submission.user_info?.name || "Unknown"}
              </span>
              {submission.responses?.[0]?.category && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium">
                  {submission.responses[0].category}
                </span>
              )}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium">
                {responses.length} response{responses.length !== 1 && "s"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
          {responses.length > 0 ? (
            responses.map((response, idx) => (
              <div
                key={idx}
                className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-cyan-200 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500 text-white text-xs font-bold flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <p className="text-sm font-semibold text-gray-800">
                        {response.question_text}
                      </p>
                      {response.question_type && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-xs font-medium">
                          {response.question_type}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg border border-gray-100">
                      {response.answer_text || (
                        <span className="italic text-gray-400">
                          No answer provided
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg
                className="w-12 h-12 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm italic">No responses recorded</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 rounded-xl transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Submission Row ───────────────────────────────────────────────────────────
const SubmissionRow = ({ submission, index }) => {
  const [isUserInfoExpanded, setIsUserInfoExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const { date, time } = formatDate(submission.submission_date);

  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-cyan-50/30 transition-colors">
        {/* Index */}
        <td className="px-4 py-4 text-center">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-cyan-500 text-white text-sm font-bold">
            {index + 1}
          </span>
        </td>

        {/* DB_ID (Submission ID) */}
        <td className="px-4 py-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-xs font-semibold">
            #{submission.submission_id}
          </span>
        </td>

        {/* Form ID (Mobile Form ID) */}
        <td className="px-4 py-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
            {submission.mobile_form_id || "—"}
          </span>
        </td>

        {/* User Info - Collapsible */}
        <td className="px-4 py-4">
          <button
            onClick={() => setIsUserInfoExpanded(!isUserInfoExpanded)}
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {submission.user_info.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900 group-hover:text-cyan-600 transition-colors">
                {submission.user_info.name}
              </p>
              <p className="text-xs text-gray-500">
                {submission.user_info.country}
              </p>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isUserInfoExpanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </td>

        {/* Category */}
        <td className="px-4 py-4">
          <div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-teal-50 text-teal-700 text-xs font-medium">
              {submission.responses?.[0]?.category || "—"}
            </span>
          </div>
        </td>

        {/* Sub Category */}
        <td className="px-4 py-4">
          <div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-xs font-medium">
              {submission.responses?.[0]?.sub_category || "—"}
            </span>
          </div>
        </td>

        {/* Submission Date */}
        <td className="px-4 py-4">
          <div>
            {(() => {
              // Example: "Wed, 08 Apr 2026 11:32:02 GMT"
              const createdAt = submission.created_at || "";
              let datePart = createdAt;
              let timePart = "";
              if (createdAt) {
                // Try to parse with Date
                const d = new Date(createdAt);
                if (!isNaN(d.getTime())) {
                  // Format: yyyy-mm-dd and HH:mm:ss
                  datePart = d.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                  timePart = d.toLocaleTimeString(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  });
                } else {
                  // Fallback: split by spaces, "Wed, 08 Apr 2026 11:32:02 GMT"
                  const tokens = createdAt.split(" ");
                  if (tokens.length >= 5) {
                    datePart = tokens.slice(0, 4).join(" ");
                    timePart = tokens[4];
                  }
                }
              }
              return (
                <>
                  <p className="font-medium text-gray-900">{datePart}</p>
                  <p className="text-xs text-gray-500">{timePart}</p>
                </>
              );
            })()}
          </div>
        </td>

        {/* Audio */}
        <td className="px-4 py-4">
          {submission.mobile_form_id ? (
            <audio
              controls
              src={`${SERVER_URL}/get_audio?&form_id=${submission.mobile_form_id}`}
              className="w-36 h-8"
            >
              Your browser does not support the audio element.
            </audio>
          ) : (
            <span className="text-gray-400 text-sm italic">No audio</span>
          )}
        </td>

        {/* Responses — opens modal */}
        <td className="px-4 py-4">
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-sm font-medium transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            {submission.responses?.length || 0} Responses
            <svg
              className="w-3.5 h-3.5 opacity-60"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        </td>
      </tr>

      {/* Expanded User Info Row */}
      {isUserInfoExpanded && (
        <tr className="bg-gray-50">
          <td colSpan="9" className="px-4 py-4">
            <div className="ml-12 p-4 bg-white rounded-xl border border-gray-200 shadow-sm animate-fadeIn">
              <h4 className="text-sm font-semibold text-cyan-600 mb-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                User Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                    Country
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {submission.user_info.country || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                    Region
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {submission.user_info.region || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                    Location
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {submission.user_info.location || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                    Mobile Form ID
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {submission.mobile_form_id || "—"}
                  </p>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* Responses Modal */}
      {isModalOpen && (
        <ResponsesModal
          submission={submission}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

const PER_PAGE = 20;

const ViewData = () => {
  const { HasPermission } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const hasAccess =
    HasPermission("QualitativeAssessmentsManagementBeta", "view_data") ||
    HasPermission("QualitativeAssessmentsManagementBeta", "full_access");

  useEffect(() => {
    if (!hasAccess) {
      toast.error("You don't have permission to access this page.");
      navigate("/qualitative-assessments-latest", { replace: true });
    }
  }, [hasAccess, navigate]);
  // Raw filter inputs — update instantly as the user types
  const [filters, setFilters] = useState({
    country: "",
    category: "",
    year: "",
    start_date: "",
    end_date: "",
  });

  // Debounced filters sent to the API — updated 3 s after the last keystroke
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  // Whenever the user edits a filter, start (or restart) the 3-second timer
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1); // reset to first page on any filter change
      setDebouncedFilters(filters);
    }, 3000);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  // Fetch whenever the debounced filters or the current page changes
  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters, page]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append("page", page);
      queryParams.append("per_page", PER_PAGE);

      const response = await fetch(
        `${SERVER_URL}/get_submission_responses?${queryParams}`,
      );
      if (!response.ok) throw new Error("Failed to fetch submissions");

      const data = await response.json();

      setTotalSubmissions(data.total_submissions ?? 0);
      setTotalPages(data.total_pages ?? 1);

      // Sort within the current page by newest first (fallback to id desc)
      const sortedSubmissions = [...(data.data || [])].sort((a, b) => {
        const dateA = new Date(a.submission_date);
        const dateB = new Date(b.submission_date);
        if (!isNaN(dateA) && !isNaN(dateB)) return dateB - dateA;
        return (b.submission_id || 0) - (a.submission_id || 0);
      });
      setSubmissions(sortedSubmissions);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submission data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const empty = {
      country: "",
      category: "",
      year: "",
      start_date: "",
      end_date: "",
    };
    setFilters(empty);
    setDebouncedFilters(empty);
    setPage(1);
    toast.info("Filters have been reset");
  };

  const [exporting, setExporting] = useState(false);

  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL records (bypass pagination)
      const queryParams = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append("page", 1);
      queryParams.append("per_page", 100000);

      const response = await fetch(
        `${SERVER_URL}/get_submission_responses?${queryParams}`,
      );
      if (!response.ok) throw new Error("Export fetch failed");
      const data = await response.json();
      const allSubmissions = data.data || [];

      // ── Step 1: Build an ordered list of unique question TEXTS across all submissions.
      // Each submission reuses the same question texts but assigns new question_ids,
      // so we must key by question_text — NOT question_id — to collapse all submissions
      // into the same set of columns.
      // We track insertion order so column order matches the first submission's question order.
      const questionTextSet = new Set(); // ordered unique question texts
      allSubmissions.forEach((submission) => {
        (submission.responses || []).forEach((r) => {
          const text = (r.question_text || "").trim();
          if (text) questionTextSet.add(text);
        });
      });
      const orderedQuestionTexts = [...questionTextSet]; // stable insertion order

      // ── Step 2: One row per submission, one column per unique question text.
      // For checkbox questions (multiple answers per question_text), join with "; ".
      const rows = allSubmissions.map((submission) => {
        const submissionDate =
          submission.created_at || submission.submission_date || "";
        const category = submission.responses?.[0]?.category || "";
        const subCategory = submission.responses?.[0]?.sub_category || "";

        // Collect ALL answers per question_text (supports checkbox multi-select)
        const answersByText = {};
        (submission.responses || []).forEach((r) => {
          const text = (r.question_text || "").trim();
          if (!text) return;
          if (!answersByText[text]) answersByText[text] = [];
          if (r.answer_text) answersByText[text].push(r.answer_text);
        });

        const row = {
          "Submission ID": submission.submission_id,
          "Mobile Form ID": submission.mobile_form_id || "",
          "User Name": submission.user_info?.name || "",
          Country: submission.user_info?.country || "",
          Region: submission.user_info?.region || "",
          Location: submission.user_info?.location || "",
          Category: category,
          "Sub Category": subCategory,
          "Submission Date": submissionDate,
        };

        orderedQuestionTexts.forEach((text) => {
          const answers = answersByText[text];
          row[text] = answers ? answers.join("; ") : "";
        });

        return row;
      });

      // ── Step 3: Write file with auto-sized columns
      const ws = XLSX.utils.json_to_sheet(rows);
      if (rows.length > 0) {
        const allKeys = Object.keys(rows[0]);
        ws["!cols"] = allKeys.map((key) => ({
          wch: Math.min(
            60,
            Math.max(
              key.length,
              ...rows.map((r) => String(r[key] ?? "").length),
            ),
          ),
        }));
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Submissions");
      XLSX.writeFile(wb, "submission_responses.xlsx");
      toast.success(
        `Exported ${allSubmissions.length} submissions (${orderedQuestionTexts.length} question columns)`,
      );
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed — please try again");
    } finally {
      setExporting(false);
    }
  };

  // Whether any filter is actively pending debounce
  const isFilterPending =
    JSON.stringify(filters) !== JSON.stringify(debouncedFilters);

  // Pagination helpers
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    for (
      let i = Math.max(1, page - delta);
      i <= Math.min(totalPages, page + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="min-h-screen p-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-5 pb-3 border-b border-slate-200">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/qualitative-assessment-dashboard")}
            className="flex items-center justify-center transition-colors w-9 h-9 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200"
          >
            <FaArrowLeftLong size={14} />
          </button>

          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-transparent bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text">
              Submission Data
            </h1>
            <p className="text-gray-600 text-sm">
              Review qualitative assessment submissions with filters and export
              options.
            </p>
          </div>
        </div>
      </div>
      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-8 border border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Filters</h3>
              <p className="text-xs text-gray-500">
                {isFilterPending
                  ? "Applying filters in 3 s…"
                  : "Refine your search criteria"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 rounded-full">
            {isFilterPending && (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            )}
            {!isFilterPending && (
              <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></span>
            )}
            <span className="text-sm text-cyan-700 font-medium">
              {totalSubmissions} submission{totalSubmissions !== 1 && "s"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              name="country"
              value={filters.country}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm transition-shadow"
              placeholder="e.g., Pakistan"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm transition-shadow bg-white appearance-none"
            >
              <option value="">All Categories</option>
              <option value="Health">Health</option>
              <option value="Education">Education</option>
              <option value="WASH">WASH</option>
              <option value="Food Security">Food Security</option>
              <option value="Emergencies">Emergencies</option>
              <option value="Livelihoods">Livelihoods</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <input
              type="number"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm transition-shadow"
              placeholder="e.g., 2024"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm transition-shadow"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm transition-shadow"
            />
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl border border-gray-200 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={exportToExcel}
              disabled={exporting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-cyan-500 hover:bg-cyan-600 disabled:opacity-60 disabled:cursor-not-allowed rounded-xl shadow-sm transition-all"
            >
              {exporting ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Exporting…
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="w-14 h-14 border-4 border-cyan-200 rounded-full animate-spin border-t-cyan-500"></div>
            </div>
            <p className="mt-4 text-gray-500 font-medium">
              Loading submissions…
            </p>
          </div>
        ) : submissions.length > 0 ? (
          <>
            {/* Table Header Info */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <p className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {(page - 1) * PER_PAGE + 1}–
                  {Math.min(page * PER_PAGE, totalSubmissions)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900">
                  {totalSubmissions}
                </span>{" "}
                submission{totalSubmissions !== 1 && "s"}
              </p>
              <p className="text-xs text-gray-500">
                Sorted by{" "}
                <span className="font-medium text-cyan-600">newest first</span>
              </p>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-cyan-500 text-white">
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider w-16">
                      #
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      DB_ID
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Form ID
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      User Info
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Sub Category
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Audio
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      Responses
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {submissions.map((submission, index) => (
                    <SubmissionRow
                      key={submission.submission_id}
                      submission={submission}
                      index={(page - 1) * PER_PAGE + index}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
                <p className="text-sm text-gray-500">
                  Page{" "}
                  <span className="font-semibold text-gray-800">{page}</span> of{" "}
                  <span className="font-semibold text-gray-800">
                    {totalPages}
                  </span>
                </p>
                <div className="flex items-center gap-1">
                  {/* First */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    «
                  </button>
                  {/* Prev */}
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((num) => (
                    <button
                      key={num}
                      onClick={() => handlePageChange(num)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        num === page
                          ? "bg-cyan-500 text-white shadow-sm"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {num}
                    </button>
                  ))}

                  {/* Next */}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ›
                  </button>
                  {/* Last */}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No submissions found
            </h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your filters to see more results.
            </p>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(12px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        .animate-modalIn {
          animation: modalIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
};

export default ViewData;
